import React, { useMemo, useState } from 'react';
import { Alert, CircularProgress, List, ListItemButton, ListItemText, Paper, Stack, TextField, Typography } from '@mui/material';

import TrendReportDetailDialog from './components/TrendReportDetailDialog';
import TrendEmptyState from './components/TrendEmptyState';
import { useTrendsReports } from './hooks/useTrendsReports';
import type { TrendReport } from './types';
import { DISPLAY_FONT, TREND_COLORS } from './trendTheme';

export default function TrendsArchivePage() {
  const { reports, loading, error } = useTrendsReports();
  const [query, setQuery] = useState('');
  const [selectedReport, setSelectedReport] = useState<TrendReport | null>(null);

  const filteredReports = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return reports.filter((report) => {
      if (!normalizedQuery) return true;
      return [report.title, report.editorSummary, report.whatItMeansForUs]
        .join(' ')
        .toLowerCase()
        .includes(normalizedQuery);
    });
  }, [reports, query]);

  if (loading) {
    return <CircularProgress sx={{ color: TREND_COLORS.navy }} />;
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Stack spacing={2}>
      <Typography sx={{ fontFamily: DISPLAY_FONT, fontSize: '1.6rem', fontWeight: 700, color: TREND_COLORS.navy }}>
        Trendarkiv
      </Typography>
      <Typography sx={{ color: TREND_COLORS.textSecondary, mt: -1 }}>
        Färdiga, publicerade trendrapporter från tidigare körningar. Sök och öppna en rapport för att läsa hela analysen.
      </Typography>
      <TextField
        size="small"
        label="Sök rapporter"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />
      {filteredReports.length === 0 ? (
        <TrendEmptyState
          dense
          title="Inga rapporter i arkivet ännu"
          description="Granska rapportutkast i Redaktionellt och gör en rapport aktiv. Först då visas den som publicerad rapport i arkivet."
        />
      ) : (
      <Paper>
        <List>
          {filteredReports.map((report) => (
            <ListItemButton
              key={report._id}
              onClick={() => setSelectedReport(report)}
              sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
            >
              <ListItemText
                primary={report.title}
                secondary={`${report.issueDate?.slice(0, 10) || ''} • ${report.status}`}
              />
            </ListItemButton>
          ))}
        </List>
      </Paper>
      )}
      <TrendReportDetailDialog
        report={selectedReport}
        open={!!selectedReport}
        onClose={() => setSelectedReport(null)}
      />
    </Stack>
  );
}
